import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectSession } from '@client/model/selectors';
import { fetchFeatureFlagsRequest } from '@client/model/appConfigFeatureFlagsModel';
import { AppState } from '@client/model/store';
import { SessionType } from '@shared';

interface FeatureFlagsContextValue {
  flags: AppConfigFeatureFlags;
  /** False until the flags have arrived (or failed to), so a caller can wait instead of reading "off". */
  isLoaded: boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  flags: {},
  isLoaded: false,
});

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const dispatch = useDispatch();
  const { sessionType } = useSelector(selectSession);
  const { flags, isLoading, isLoaded } = useSelector(
    (state: AppState) => state.appConfigFeatureFlags,
  );

  const isAuthenticated = sessionType !== SessionType.unauthenticated && sessionType !== SessionType.unknown;

  useEffect(() => {
    if (isAuthenticated && !isLoaded && !isLoading) {
      dispatch(fetchFeatureFlagsRequest());
    }
  }, [isAuthenticated, isLoaded, isLoading, dispatch]);

  return (
    <FeatureFlagsContext.Provider value={{ flags, isLoaded }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export const useFeatureFlags = () => useContext(FeatureFlagsContext);
